import React, { useEffect, useState } from 'react';
import { adminClientsService, ClientUser } from '../../../services/adminClientsService';
import { toastSuccess, toastError } from '../../../lib/toast';
import { ConfirmModal, Modal } from '../../ui';
import AddUserModal from './AddUserModal';
import { SystemTenantNotice } from './SystemTenantNotice';

interface Props {
  clientId: string;
  isSystem?: boolean;
}

const ROLES = [
  { value: 'analyste', label: 'Analyste' },
  { value: 'admin_client', label: 'Admin Client' },
];

const roleBadgeClass = (role: string) =>
  role === 'admin_client'
    ? 'bg-purple-100 text-purple-800'
    : 'bg-blue-100 text-blue-800';

const roleLabel = (role: string) =>
  ROLES.find((r) => r.value === role)?.label || role;

export const UsersTab: React.FC<Props> = ({ clientId, isSystem = false }) => {
  const [users, setUsers] = useState<ClientUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Role change modal state
  const [roleEditUser, setRoleEditUser] = useState<ClientUser | null>(null);
  const [roleEditValue, setRoleEditValue] = useState('analyste');
  const [roleSubmitting, setRoleSubmitting] = useState(false);

  // Confirm remove
  const [removeUser, setRemoveUser] = useState<ClientUser | null>(null);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminClientsService.listUsers(clientId);
      setUsers(data);
    } catch (err: any) {
      setError(err?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Close action menu on outside click
  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  const handleStartRoleEdit = (user: ClientUser) => {
    setRoleEditUser(user);
    setRoleEditValue(user.role);
    setOpenMenuId(null);
  };

  const handleConfirmRoleEdit = async () => {
    if (!roleEditUser) return;
    setRoleSubmitting(true);
    try {
      await adminClientsService.updateUserRole(clientId, roleEditUser.user_id, roleEditValue);
      toastSuccess('Rôle mis à jour');
      setRoleEditUser(null);
      await load();
    } catch (err) {
      toastError('Échec de la mise à jour', err);
    } finally {
      setRoleSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeUser) return;
    setRemoveSubmitting(true);
    try {
      await adminClientsService.removeUser(clientId, removeUser.user_id);
      toastSuccess('Utilisateur retiré');
      setRemoveUser(null);
      await load();
    } catch (err) {
      toastError('Échec du retrait', err);
    } finally {
      setRemoveSubmitting(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500 py-8 text-center">Chargement...</div>;
  }
  if (error) {
    return <div className="text-red-600 py-8 text-center">{error}</div>;
  }

  return (
    <div className="space-y-4">
      {isSystem && <SystemTenantNotice what="rattachement d'utilisateurs non recommandé" />}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {users.length} utilisateur(s) rattaché(s) à ce client.
        </div>
        <button
          type="button"
          onClick={() => setOpenAdd(true)}
          disabled={isSystem}
          className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
        >
          + Rattacher un utilisateur
        </button>
      </div>

      {users.length === 0 ? (
        <div className="text-gray-500 py-8 text-center border border-dashed border-gray-300 rounded">
          Aucun utilisateur rattaché.
        </div>
      ) : (
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-primary-50 border-b-2 border-primary-200 text-left text-sm text-gray-700">
              <th className="p-3 font-semibold">Email</th>
              <th className="p-3 font-semibold">Username</th>
              <th className="p-3 font-semibold">Nom</th>
              <th className="p-3 font-semibold">Rôle</th>
              <th className="p-3 font-semibold">Statut</th>
              <th className="p-3 font-semibold w-10"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b hover:bg-gray-50 bg-white text-sm">
                <td className="p-3">{u.email}</td>
                <td className="p-3 text-gray-600">{u.username}</td>
                <td className="p-3 text-gray-600">{u.full_name || '—'}</td>
                <td className="p-3">
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${roleBadgeClass(u.role)}`}>
                    {roleLabel(u.role)}
                  </span>
                </td>
                <td className="p-3">
                  {u.is_active ? (
                    <span className="text-green-700 text-xs">Actif</span>
                  ) : (
                    <span className="text-gray-400 text-xs">Inactif</span>
                  )}
                </td>
                <td className="p-3 relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === u.user_id ? null : u.user_id);
                    }}
                    className="text-gray-500 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-100"
                    aria-label="Actions"
                  >
                    ⋮
                  </button>
                  {openMenuId === u.user_id && (
                    <div
                      className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded shadow-lg min-w-[180px]"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isSystem && (
                        <button
                          type="button"
                          onClick={() => handleStartRoleEdit(u)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                        >
                          Changer de rôle
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setRemoveUser(u);
                          setOpenMenuId(null);
                        }}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        Retirer
                      </button>
                      {/* TODO: Reset password — endpoint non disponible */}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <AddUserModal
        open={openAdd}
        clientId={clientId}
        onClose={() => setOpenAdd(false)}
        onAdded={load}
      />

      <Modal
        isOpen={roleEditUser !== null}
        onClose={() => !roleSubmitting && setRoleEditUser(null)}
        title="Changer le rôle"
        size="sm"
      >
        {roleEditUser && (
          <div className="space-y-4">
            <div className="text-sm text-gray-700">
              Utilisateur : <span className="font-medium">{roleEditUser.email}</span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nouveau rôle</label>
              <select
                value={roleEditValue}
                onChange={(e) => setRoleEditValue(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm bg-white"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRoleEditUser(null)}
                disabled={roleSubmitting}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmRoleEdit}
                disabled={roleSubmitting || roleEditValue === roleEditUser.role}
                className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary-600 disabled:opacity-50"
              >
                {roleSubmitting ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmModal
        open={removeUser !== null}
        title="Retirer l'utilisateur"
        message={
          removeUser
            ? `Retirer ${removeUser.email} du client ? Il conservera son compte global.`
            : ''
        }
        confirmLabel={removeSubmitting ? 'Suppression...' : 'Retirer'}
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleConfirmRemove}
        onCancel={() => !removeSubmitting && setRemoveUser(null)}
      />
    </div>
  );
};

export default UsersTab;
